import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeft,
  GripVertical,
  Plus,
  Trash2,
  Play,
  Save,
  Link2,
  History,
  BookOpen,
  ChevronDown,
  Check,
  Download,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useSpec, useCreateSpec, useUpdateSpec } from '../hooks/useSpecifications';
import { exportMarkdown } from '../services/specificationService';
import RequirementTreeSelect from '../components/requirement/RequirementTreeSelect';
import type { ProjectDetail } from '../services/projectService';
import type { FlowStep, DetailSpecField } from '@maios/shared';
import Spinner from '../components/ui/Spinner';
import TestCaseGenerateModal from '../components/spec/TestCaseGenerateModal';
import SpecDiffModal from '../components/spec/SpecDiffModal';
import GlossaryPanel from '../components/spec/GlossaryPanel';

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: '초안' },
  { value: 'IN_REVIEW', label: '검토 중' },
  { value: 'APPROVED', label: '승인됨' },
  { value: 'IMPLEMENTED', label: '구현됨' },
];

// --- Sortable Flow Step Component ---
function SortableFlowStep({
  id,
  step,
  onUpdate,
  onRemove,
}: {
  id: string;
  step: FlowStep;
  onUpdate: (field: keyof FlowStep, value: string | number) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 group"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="mt-2.5 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
      >
        <GripVertical className="size-4" />
      </button>
      <span className="mt-2.5 text-sm font-mono text-gray-400 w-6 text-right shrink-0">
        {step.step}.
      </span>
      <input
        value={step.action}
        onChange={(e) => onUpdate('action', e.target.value)}
        placeholder="단계 설명을 입력하세요..."
        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
      />
      <button
        type="button"
        onClick={onRemove}
        className="mt-2 p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <Trash2 className="size-4" />
      </button>
    </div>
  );
}

// --- Flow Editor Component ---
function FlowEditor({
  label,
  steps,
  onChange,
  prefix,
}: {
  label: string;
  steps: FlowStep[];
  onChange: (steps: FlowStep[]) => void;
  prefix?: string;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const ids = steps.map((_, i) => `${prefix ?? 'step'}-${i}`);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = ids.indexOf(active.id as string);
      const newIndex = ids.indexOf(over.id as string);
      const reordered = arrayMove(steps, oldIndex, newIndex).map((s, i) => ({
        ...s,
        step: i + 1,
      }));
      onChange(reordered);
    }
  };

  const addStep = () => {
    onChange([...steps, { step: steps.length + 1, action: '' }]);
  };

  const updateStep = (index: number, field: keyof FlowStep, value: string | number) => {
    const updated = [...steps];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeStep = (index: number) => {
    onChange(steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, step: i + 1 })));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-gray-700">{label}</h4>
        <button
          type="button"
          onClick={addStep}
          className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
        >
          <Plus className="size-3.5" /> 단계 추가
        </button>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {steps.map((step, i) => (
              <SortableFlowStep
                key={ids[i]}
                id={ids[i]}
                step={step}
                onUpdate={(field, value) => updateStep(i, field, value)}
                onRemove={() => removeStep(i)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {steps.length === 0 && (
        <button
          type="button"
          onClick={addStep}
          className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-400 hover:border-primary-400 hover:text-primary-500 transition-colors"
        >
          + 단계 추가
        </button>
      )}
    </div>
  );
}

// CHG-007: No 채번 로직 — 1부터 순회하며 비어있는 가장 작은 번호를 반환
function getNextNo(existingNos: number[]): number {
  let no = 1;
  const sorted = [...existingNos].sort((a, b) => a - b);
  for (const existing of sorted) {
    if (no < existing) return no;
    no = existing + 1;
  }
  return no;
}

// CHG-006/007: 기능 타입 드롭다운 옵션
const FUNC_TYPE_OPTIONS = ['Button', 'Input-box', 'Modal', 'Bottomsheet', 'Accordion'];

// 기능 타입 배지 색상
const FUNC_TYPE_BADGE: Record<string, string> = {
  'Button': 'bg-blue-100 text-blue-700',
  'Input-box': 'bg-emerald-100 text-emerald-700',
  'Modal': 'bg-purple-100 text-purple-700',
  'Bottomsheet': 'bg-amber-100 text-amber-700',
  'Accordion': 'bg-orange-100 text-orange-700',
};

// --- CHG-005/006/007: Detail Spec Accordion Component ---
function DetailSpecTable({
  fields,
  onChange,
}: {
  fields: DetailSpecField[];
  onChange: (fields: DetailSpecField[]) => void;
}) {
  const [expandedNo, setExpandedNo] = useState<number | null>(null);
  const [editingNameIdx, setEditingNameIdx] = useState<number | null>(null);
  const [editingTypeIdx, setEditingTypeIdx] = useState<number | null>(null);

  const addField = () => {
    const nextNo = getNextNo(fields.map((f) => f.no));
    onChange([...fields, {
      no: nextNo,
      fieldName: '',
      type: 'Button',
      required: false,
      constraint: '',
      content: '',
      actionLogic: '',
    }]);
    setExpandedNo(nextNo);
    // 새 필드 기능명 바로 편집
    setTimeout(() => setEditingNameIdx(fields.length), 0);
  };

  const updateField = (index: number, key: keyof DetailSpecField, value: string | boolean | number) => {
    const updated = [...fields];
    updated[index] = { ...updated[index], [key]: value };
    onChange(updated);
  };

  const removeField = (index: number) => {
    if (!confirm('이 항목을 삭제하시겠습니까?')) return;
    const removedNo = fields[index].no;
    onChange(fields.filter((_, i) => i !== index));
    if (expandedNo === removedNo) setExpandedNo(null);
  };

  const toggleExpand = (no: number) => {
    setExpandedNo((prev) => (prev === no ? null : no));
  };

  // textarea auto-resize
  const handleTextareaInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  return (
    <div>
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-700">
          상세 명세 <span className="font-normal text-gray-400">({fields.length}개 항목)</span>
        </h4>
        <button
          type="button"
          onClick={addField}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
        >
          <Plus className="size-3.5" /> 필드 추가
        </button>
      </div>

      {fields.length > 0 ? (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {fields.map((field, i) => {
            const isExpanded = expandedNo === field.no;
            const badgeClass = FUNC_TYPE_BADGE[field.type] ?? 'bg-gray-100 text-gray-600';

            return (
              <div key={field.no} className={isExpanded ? 'border-l-2 border-l-blue-500' : ''}>
                {/* 요약 행 */}
                <div
                  onClick={() => toggleExpand(field.no)}
                  className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer select-none transition-colors ${
                    isExpanded ? 'bg-blue-50' : 'bg-white hover:bg-gray-50'
                  } ${i > 0 ? 'border-t border-gray-200' : ''}`}
                >
                  {/* No */}
                  <span className="text-sm font-mono text-gray-400 w-6 text-center shrink-0">{field.no}</span>

                  {/* 기능명 (인라인 편집) */}
                  <div className="min-w-[140px] flex-1" onClick={(e) => e.stopPropagation()}>
                    {editingNameIdx === i ? (
                      <input
                        autoFocus
                        value={field.fieldName}
                        onChange={(e) => updateField(i, 'fieldName', e.target.value)}
                        onBlur={() => setEditingNameIdx(null)}
                        onKeyDown={(e) => { if (e.key === 'Enter') setEditingNameIdx(null); }}
                        maxLength={100}
                        placeholder="기능명 입력"
                        className="w-full px-2 py-0.5 border border-primary-300 rounded text-sm focus:ring-1 focus:ring-primary-500 outline-none"
                      />
                    ) : (
                      <span
                        onClick={() => setEditingNameIdx(i)}
                        className={`text-sm cursor-text ${field.fieldName ? 'text-gray-900' : 'text-gray-400 italic'}`}
                      >
                        {field.fieldName || '기능명 입력'}
                      </span>
                    )}
                  </div>

                  {/* 필수 체크 */}
                  <div className="shrink-0 w-6 text-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => updateField(i, 'required', !field.required)}
                      className={`size-5 rounded border inline-flex items-center justify-center transition-colors ${
                        field.required
                          ? 'bg-primary-600 border-primary-600 text-white'
                          : 'bg-white border-gray-300 text-transparent hover:border-gray-400'
                      }`}
                      title={field.required ? '필수' : '선택'}
                    >
                      <Check className="size-3" />
                    </button>
                  </div>

                  {/* 기능 타입 배지 (클릭 시 드롭다운) */}
                  <div className="shrink-0 w-24 text-center" onClick={(e) => e.stopPropagation()}>
                    {editingTypeIdx === i ? (
                      <select
                        autoFocus
                        value={FUNC_TYPE_OPTIONS.includes(field.type) ? field.type : ''}
                        onChange={(e) => { updateField(i, 'type', e.target.value); setEditingTypeIdx(null); }}
                        onBlur={() => setEditingTypeIdx(null)}
                        className="px-2 py-0.5 border border-primary-300 rounded text-sm focus:ring-1 focus:ring-primary-500 outline-none"
                      >
                        {!FUNC_TYPE_OPTIONS.includes(field.type) && field.type && (
                          <option value={field.type}>{field.type} (기존)</option>
                        )}
                        {FUNC_TYPE_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    ) : (
                      <span
                        onClick={() => setEditingTypeIdx(i)}
                        className={`inline-block px-2 py-0.5 rounded-full text-sm font-medium cursor-pointer ${badgeClass}`}
                      >
                        {field.type || '타입'}
                      </span>
                    )}
                  </div>

                  {/* 내용 (truncate) */}
                  <span className="flex-1 text-sm text-gray-500 truncate min-w-0">
                    {field.content || ''}
                  </span>

                  {/* 펼침 아이콘 */}
                  <ChevronDown
                    className={`size-4 text-gray-400 shrink-0 transition-transform duration-200 ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                  />
                </div>

                {/* 상세 영역 (펼쳤을 때) */}
                {isExpanded && (
                  <div className="bg-slate-50 border-t border-gray-200 px-5 py-4 space-y-3">
                    {/* 노출 조건 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">노출 조건</label>
                      <textarea
                        value={field.constraint}
                        onChange={(e) => updateField(i, 'constraint', e.target.value)}
                        onInput={handleTextareaInput}
                        placeholder="해당 기능이 노출되는 조건을 입력하세요"
                        rows={1}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none"
                      />
                    </div>
                    {/* 내용 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">내용</label>
                      <textarea
                        value={field.content}
                        onChange={(e) => updateField(i, 'content', e.target.value)}
                        onInput={handleTextareaInput}
                        placeholder="기능에 대한 상세 설명을 입력하세요"
                        rows={2}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none"
                      />
                    </div>
                    {/* 동작 로직 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">동작 로직</label>
                      <textarea
                        value={field.actionLogic}
                        onChange={(e) => updateField(i, 'actionLogic', e.target.value)}
                        onInput={handleTextareaInput}
                        placeholder="해당 기능의 동작 방식을 기술하세요"
                        rows={2}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none"
                      />
                    </div>
                    {/* 하단 버튼 */}
                    <div className="flex justify-end pt-1">
                      <button
                        type="button"
                        onClick={() => removeField(i)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                      >
                        <Trash2 className="size-3.5" />
                        삭제
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <button
          type="button"
          onClick={addField}
          className="w-full py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-400 hover:border-primary-400 hover:text-primary-500 transition-colors"
        >
          + 첫 번째 필드를 추가하세요
        </button>
      )}
    </div>
  );
}

// ==================== MAIN PAGE ====================
export default function SpecEditPage() {
  const { key, specId } = useParams<{ key: string; specId: string }>();
  const { project } = useOutletContext<{ project: ProjectDetail }>();
  const navigate = useNavigate();
  const isNew = specId === 'new';

  // Fetch existing spec if editing
  const { data: existingSpec, isLoading: specLoading } = useSpec(
    project.id,
    isNew ? '' : specId!,
  );

  const createMutation = useCreateSpec(project.id);
  const updateMutation = useUpdateSpec(project.id);

  // --- Form State ---
  const [requirementId, setRequirementId] = useState('');
  const [functionCode, setFunctionCode] = useState('');
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState('DRAFT');
  const [actor, setActor] = useState('');
  const [precondition, setPrecondition] = useState('');
  const [postcondition, setPostcondition] = useState('');
  const [mainFlow, setMainFlow] = useState<FlowStep[]>([]);
  const [alternativeFlow, setAlternativeFlow] = useState<FlowStep[]>([]);
  const [exceptionFlow, setExceptionFlow] = useState<FlowStep[]>([]);
  const [inputSpec, setInputSpec] = useState<DetailSpecField[]>([]);
  const [assigneeId, setAssigneeId] = useState('');
  const [uiReference, setUiReference] = useState('');
  const [businessRules, setBusinessRules] = useState('');

  const [showTCModal, setShowTCModal] = useState(false);
  const [showDiffModal, setShowDiffModal] = useState(false);
  const [showGlossary, setShowGlossary] = useState(false);
  const [isExporting, setIsExporting] = useState(false);


  // Populate form when editing
  useEffect(() => {
    if (existingSpec) {
      setRequirementId(existingSpec.requirementId);
      setFunctionCode(existingSpec.functionCode);
      setTitle(existingSpec.title);
      setStatus(existingSpec.status);
      setActor(existingSpec.actor ?? '');
      setPrecondition(existingSpec.precondition ?? '');
      setPostcondition(existingSpec.postcondition ?? '');
      setMainFlow(existingSpec.mainFlow ?? []);
      setAlternativeFlow(existingSpec.alternativeFlow ?? []);
      setExceptionFlow(existingSpec.exceptionFlow ?? []);
      // CHG-006: 기존 데이터 하위 호환 — content, actionLogic 없으면 "" 기본값
      setInputSpec(
        (existingSpec.inputSpec ?? []).map((raw, i) => {
          const f = raw as unknown as Record<string, unknown>;
          return {
            no: (f.no as number) ?? i + 1,
            fieldName: (f.fieldName as string) ?? '',
            type: (f.type as string) ?? '',
            required: (f.required as boolean) ?? false,
            constraint: (f.constraint as string) ?? (f.constraints as string) ?? '',
            content: (f.content as string) ?? '',
            actionLogic: (f.actionLogic as string) ?? '',
          };
        }),
      );
      setAssigneeId(existingSpec.assigneeId ?? '');
      setUiReference(existingSpec.uiReference ?? '');
      setBusinessRules((existingSpec.businessRules ?? []).join('\n'));
    }
  }, [existingSpec]);

  const buildPayload = useCallback(() => {
    return {
      requirementId,
      functionCode: functionCode.trim(),
      title: title.trim(),
      status: status as 'DRAFT' | 'IN_REVIEW' | 'APPROVED' | 'IMPLEMENTED',
      actor: actor || undefined,
      precondition: precondition || undefined,
      postcondition: postcondition || undefined,
      mainFlow: mainFlow.length > 0 ? mainFlow.filter((s) => s.action.trim()) : undefined,
      alternativeFlow: alternativeFlow.length > 0 ? alternativeFlow.filter((s) => s.action.trim()) : undefined,
      exceptionFlow: exceptionFlow.length > 0 ? exceptionFlow.filter((s) => s.action.trim()) : undefined,
      inputSpec: inputSpec.length > 0 ? inputSpec.filter((f) => f.fieldName.trim()) : undefined,
      businessRules: businessRules.trim()
        ? businessRules.split('\n').filter((r) => r.trim())
        : undefined,
      uiReference: uiReference || undefined,
      assigneeId: assigneeId || undefined,
    };
  }, [
    requirementId, functionCode, title, status, actor,
    precondition, postcondition, mainFlow, alternativeFlow,
    exceptionFlow, inputSpec, businessRules, uiReference, assigneeId,
  ]);

  const handleSave = async () => {
    if (!requirementId) {
      toast.error('연결된 요구사항을 선택해주세요.');
      return;
    }
    if (!functionCode.trim()) {
      toast.error('기능 코드를 입력해주세요.');
      return;
    }
    if (!title.trim()) {
      toast.error('제목을 입력해주세요.');
      return;
    }

    const payload = buildPayload();

    try {
      if (isNew) {
        const created = await createMutation.mutateAsync(payload);
        toast.success('기능 명세가 생성되었습니다.');
        navigate(`/projects/${key}/specs/${created.id}`, { replace: true });
      } else {
        await updateMutation.mutateAsync({ specId: specId!, data: payload });
        toast.success('기능 명세가 저장되었습니다.');
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg ?? '저장에 실패했습니다.');
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const handleExportMarkdown = async () => {
    if (!specId || isNew) return;
    setIsExporting(true);
    try {
      await exportMarkdown(project.id, specId);
      toast.success('마크다운 파일이 다운로드되었습니다.');
    } catch {
      toast.error('마크다운 추출에 실패했습니다.');
    } finally {
      setIsExporting(false);
    }
  };

  if (!isNew && specLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="size-8" />
      </div>
    );
  }

  return (
    <div className="flex gap-6">
    <div className="flex-1 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(`/projects/${key}/specifications`)}
          className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="size-4" />
        </button>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-900">
            {isNew ? '새 기능 명세' : `${existingSpec?.functionCode}: ${existingSpec?.title}`}
          </h2>
        </div>
        <button
          type="button"
          onClick={() => setShowGlossary(!showGlossary)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            showGlossary
              ? 'bg-primary-50 text-primary-700 border border-primary-200'
              : 'text-gray-500 hover:bg-gray-100 border border-gray-200'
          }`}
          title="용어 사전 패널"
        >
          <BookOpen className="size-3.5" />
          용어 사전
        </button>
      </div>

      {/* Form */}
      <div className="space-y-6">
        {/* Section: 기본 정보 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
          {/* 연결된 요구사항 (CHG-029: 트리 선택) */}
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
              <Link2 className="size-4" /> 연결된 요구사항 *
            </label>
            <RequirementTreeSelect
              projectId={project.id}
              value={requirementId || null}
              onChange={(id) => setRequirementId(id ?? '')}
              placeholder="요구사항을 선택하세요"
            />
          </div>

          {/* 기능 코드 + 상태 + 담당자 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">기능 코드 *</label>
              <input
                value={functionCode}
                onChange={(e) => setFunctionCode(e.target.value.toUpperCase())}
                placeholder="FN-PRJ-001"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">상태</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">제목 *</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="기능 명세 제목"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">담당자</label>
              <select
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
              >
                <option value="">미할당</option>
                {project.members.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.user.name} ({m.projectRole})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Section: Actor, 조건 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Actor</label>
            <input
              value={actor}
              onChange={(e) => setActor(e.target.value)}
              placeholder="수행 주체 (예: 로그인한 PM 사용자)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">사전조건</label>
              <textarea
                value={precondition}
                onChange={(e) => setPrecondition(e.target.value)}
                rows={2}
                placeholder="이 기능 실행 전 충족 조건"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">사후조건</label>
              <textarea
                value={postcondition}
                onChange={(e) => setPostcondition(e.target.value)}
                rows={2}
                placeholder="기능 실행 후 결과 상태"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none"
              />
            </div>
          </div>
        </div>

        {/* Section: 주요 흐름 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <FlowEditor
            label="주요 흐름 (Main Flow)"
            steps={mainFlow}
            onChange={setMainFlow}
            prefix="main"
          />
        </div>

        {/* Section: 대안 흐름 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <FlowEditor
            label="대안 흐름 (Alternative Flow)"
            steps={alternativeFlow}
            onChange={setAlternativeFlow}
            prefix="alt"
          />
        </div>

        {/* Section: 예외 흐름 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <FlowEditor
            label="예외 흐름 (Exception Flow)"
            steps={exceptionFlow}
            onChange={setExceptionFlow}
            prefix="exc"
          />
        </div>

        {/* Section: 상세 명세 (CHG-005) */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <DetailSpecTable fields={inputSpec} onChange={setInputSpec} />
        </div>

        {/* Section: 비즈니스 규칙 + UI 참조 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">비즈니스 규칙</label>
            <textarea
              value={businessRules}
              onChange={(e) => setBusinessRules(e.target.value)}
              rows={3}
              placeholder="비즈니스 규칙을 줄바꿈으로 구분하여 입력"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">UI 참조 링크</label>
            <input
              value={uiReference}
              onChange={(e) => setUiReference(e.target.value)}
              placeholder="피그마 링크 등"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            />
          </div>
        </div>

        {/* Bottom buttons */}
        <div className="flex items-center justify-between py-4">
          <button
            type="button"
            onClick={() => navigate(`/projects/${key}/specifications`)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            취소
          </button>
          <div className="flex items-center gap-3">
            {!isNew && (
              <button
                type="button"
                onClick={() => setShowDiffModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <History className="size-4" />
                변경 이력
              </button>
            )}
            {!isNew && (
              <button
                type="button"
                onClick={handleExportMarkdown}
                disabled={isExporting}
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                {isExporting ? <Spinner className="size-4" /> : <Download className="size-4" />}
                마크다운 추출
              </button>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-5 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {isSaving ? <Spinner className="size-4 text-white" /> : <Save className="size-4" />}
              저장
            </button>
            {!isNew && (
              <button
                type="button"
                onClick={() => setShowTCModal(true)}
                className="inline-flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
              >
                <Play className="size-4" />
                테스트케이스 생성
              </button>
            )}
          </div>
        </div>
      </div>

      {/* TC Generation Modal */}
      {showTCModal && specId && !isNew && (
        <TestCaseGenerateModal
          projectId={project.id}
          specId={specId}
          onClose={() => setShowTCModal(false)}
        />
      )}

      {/* Diff Modal */}
      {showDiffModal && specId && !isNew && (
        <SpecDiffModal
          projectId={project.id}
          specId={specId}
          currentVersion={existingSpec?.version ?? 1}
          onClose={() => setShowDiffModal(false)}
        />
      )}
    </div>

    {/* Glossary Panel */}
    {showGlossary && (
      <GlossaryPanel
        onClose={() => setShowGlossary(false)}
      />
    )}
    </div>
  );
}
